import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import AdminLayout from '../../components/Admin/AdminLayout'
import PaymentReviewModal from '../../components/Admin/PaymentReviewModal'
import { trendDirFromPct } from './adminUi'
import { resolveIconNode } from '../../components/icons'

const PAGE_SIZE = 10

const PERIOD_TABS = [
  { id: 'mtd', label: 'Month to Date' },
  { id: 'qtd', label: 'Quarter to Date' },
  { id: 'ytd', label: 'Year to Date' },
]

const PERIOD_LABELS = { mtd: 'MTD', qtd: 'QTD', ytd: 'YTD' }

function StatCard({ icon, cls, label, value, trend, trendDir = 'up' }) {
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-card-top">
        <span className={`admin-stat-icon ${cls}`}>{resolveIconNode(icon, { size: 'md' })}</span>
        {trend && <span className={`admin-kpi-trend ${trendDir}`}>{trend}</span>}
      </div>
      <div className="admin-stat-value">{value}</div>
      <div className="admin-stat-label">{label}</div>
    </div>
  )
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function AdminPayments() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [period, setPeriod] = useState('mtd')
  const [planFilter, setPlanFilter] = useState('all')
  const [roleFilter, setRoleFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [reviewOrderId, setReviewOrderId] = useState(searchParams.get('review') || null)

  const load = (nextPeriod = period) => {
    setLoading(true)
    axios.get('/api/admin/payments', { params: { period: nextPeriod } })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load payments'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load(period)
  }, [period])

  const handlePeriodChange = (nextPeriod) => {
    setPeriod(nextPeriod)
    setPage(1)
  }

  useEffect(() => {
    const review = searchParams.get('review')
    if (review) setReviewOrderId(review)
  }, [searchParams])

  const filtered = useMemo(() => {
    let rows = data?.transactions || []
    if (planFilter !== 'all') {
      rows = rows.filter((r) => (r.plan || '').toLowerCase() === planFilter)
    }
    if (roleFilter !== 'all') {
      rows = rows.filter((r) => r.role === roleFilter || (roleFilter === 'Advisor' && r.role === 'ServiceProvider'))
    }
    return rows
  }, [data, planFilter, roleFilter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const stats = data?.stats || {}
  const periodLabel = data?.periodLabel || PERIOD_LABELS[period] || 'MTD'

  return (
    <AdminLayout title="Payments">
      <div className="admin-period-bar">
        <div className="admin-period-tabs">
          {PERIOD_TABS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`admin-period-tab${period === p.id ? ' active' : ''}`}
              onClick={() => handlePeriodChange(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-stats-row cols-4">
        <StatCard icon="$" cls="green" label={`Total Revenue (${periodLabel})`} value={loading && !data ? '…' : (stats.totalRevenue ?? stats.totalRevenueMtd ?? '—')} trend={stats.revenueTrend} trendDir={trendDirFromPct(stats.revenueTrend)} />
        <StatCard icon="payment" cls="blue" label="Transactions" value={loading && !data ? '…' : (stats.totalTransactions ?? '—')} trend={stats.transactionsTrend} trendDir={trendDirFromPct(stats.transactionsTrend)} />
        <StatCard icon="users" cls="purple" label="Active Payers" value={loading && !data ? '…' : (stats.activePayers ?? '—')} trend={stats.payersTrend} trendDir={trendDirFromPct(stats.payersTrend)} />
        <StatCard icon="chart" cls="orange" label="Avg. Transaction" value={loading && !data ? '…' : (stats.avgTransaction ?? '—')} trend={stats.avgTrend} trendDir={trendDirFromPct(stats.avgTrend)} />
      </div>

      {(data?.pendingOrders || []).length > 0 && (
        <div className="admin-table-card" style={{ marginBottom: 24, border: '1px solid #f59e0b' }}>
          <div className="admin-table-header">
            <span className="admin-table-title">Awaiting payment confirmation ({data.pendingOrders.length})</span>
            <span style={{ fontSize: 12, color: '#909399' }}>User reported WeChat transfer — verify and activate</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Company</th>
                <th>Role</th>
                <th>Period</th>
                <th>Amount</th>
                <th>Reported</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.pendingOrders.map((o) => (
                <tr key={o.id}>
                  <td>
                    <strong>{o.system_name}</strong>
                    <div style={{ fontSize: 11, color: '#909399' }}>{o.work_email}</div>
                  </td>
                  <td>{o.company_name || '—'}</td>
                  <td>{o.role}</td>
                  <td><strong>{o.period_label}</strong></td>
                  <td>{o.amount_display}</td>
                  <td>{formatDate(o.updated_at || o.created_at)}</td>
                  <td className="admin-td-actions">
                    <div className="admin-action-btns">
                    <button
                      type="button"
                      className="admin-btn admin-btn-sm admin-btn-primary"
                      onClick={() => setReviewOrderId(o.id)}
                    >
                      Review & approve
                    </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="admin-content-grid">
        <div className="admin-card">
          <div className="admin-card-header">
            <span className="admin-card-title">
              {period === 'qtd' ? 'Quarterly Revenue' : period === 'ytd' ? 'Year-to-Date Revenue' : 'Monthly Revenue'}
            </span>
          </div>
          <div className="admin-chart-bars">
            {(data?.monthly || []).map((m) => (
              <div key={m.label} className="admin-bar-col">
                <div className="admin-bar single" style={{ height: `${m.height}%`, background: 'linear-gradient(180deg,#1a5fd1,#0f3d8a)' }} />
                <span className="admin-bar-label">{m.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="admin-card">
          <div className="admin-card-header">
            <span className="admin-card-title">Revenue by Plan</span>
          </div>
          <div className="admin-breakdown-list">
            {(data?.planBreakdown || []).map((b) => (
              <div key={b.name} className="admin-breakdown-item">
                <div className="admin-breakdown-info">
                  <div className="admin-breakdown-name">{b.name}</div>
                </div>
                <div className="admin-breakdown-right">
                  <div className="admin-breakdown-amount">{b.amount}</div>
                  <div className="admin-breakdown-pct">{b.pct}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="admin-content-grid">
        <div className="admin-card">
          <div className="admin-card-header">
            <span className="admin-card-title">Revenue by Role</span>
          </div>
          <div className="admin-breakdown-list">
            {(data?.roleBreakdown || []).map((b) => (
              <div key={b.name} className="admin-breakdown-item">
                <div className="admin-breakdown-info">
                  <div className="admin-breakdown-name">{b.name}</div>
                </div>
                <div className="admin-breakdown-right">
                  <div className="admin-breakdown-amount">{b.amount}</div>
                  <div className="admin-breakdown-pct">{b.pct}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="admin-table-card">
        <div className="admin-table-header">
          <span className="admin-table-title">Recent Transactions</span>
        </div>
        <div className="admin-filter-bar">
          <select value={planFilter} onChange={(e) => { setPlanFilter(e.target.value); setPage(1) }}>
            <option value="all">All Plans</option>
            <option value="basic">Basic</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </select>
          <select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1) }}>
            <option value="all">All Roles</option>
            <option value="GP">GP</option>
            <option value="LP">LP</option>
            <option value="Startup">Startup</option>
            <option value="Advisor">Advisor</option>
          </select>
        </div>
        <table>
          <thead>
            <tr><th>User</th><th>Role</th><th>Plan</th><th>Amount</th><th>Status</th><th>Date</th></tr>
          </thead>
          <tbody>
            {pageItems.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#909399', padding: 32 }}>No transactions found</td></tr>
            ) : pageItems.map((r) => (
              <tr key={r.id}>
                <td>
                  <strong>{r.display_name || r.system_name}</strong>
                  <div style={{ fontSize: 11, color: '#909399' }}>{r.work_email}</div>
                </td>
                <td><span className={`admin-role-badge ${(r.role || '').toLowerCase()}`}>{r.role}</span></td>
                <td>{r.plan}</td>
                <td>{r.amount_display}</td>
                <td><span className={`admin-status-badge ${r.status_key}`}>{r.status}</span></td>
                <td>{formatDate(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="admin-pagination">
          <span className="admin-pg-info">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className="admin-pg-controls">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</button>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
          </div>
        </div>
      </div>

      {reviewOrderId && (
        <PaymentReviewModal
          orderId={reviewOrderId}
          onClose={() => {
            setReviewOrderId(null)
            setSearchParams({})
          }}
          onApproved={() => {
            load(period)
            window.dispatchEvent(new Event('rs-admin-badges-changed'))
          }}
        />
      )}
    </AdminLayout>
  )
}
